export const MAX_INTEGER_DIGITS = 36;
/** Number pad when no fraction digits are allowed, decimal pad otherwise. */
export const getMoneyKeyboardType = (maximumFractionalDigits = 2) => maximumFractionalDigits > 0 ? 'decimal-pad' : 'number-pad';
const JS_DECIMAL_SEPARATOR = '.';
let systemDecimalSeparator;
/** The device locale's decimal separator, or "." where Hermes lacks `formatToParts` (iOS). */
export const getSystemDecimalSeparator = () => {
    if (systemDecimalSeparator === undefined) {
        try {
            systemDecimalSeparator =
                new Intl.NumberFormat()
                    .formatToParts?.(1.1)
                    .find((part) => part.type === 'decimal')?.value ??
                    JS_DECIMAL_SEPARATOR;
        }
        catch {
            systemDecimalSeparator = JS_DECIMAL_SEPARATOR;
        }
    }
    return systemDecimalSeparator;
};
const replaceAll = (text, search, replacement) => search === '' ? text : text.split(search).join(replacement);
const removePrefix = (text, prefix) => prefix !== '' && text.startsWith(prefix) ? text.slice(prefix.length) : text;
const removeSuffix = (text, suffix) => suffix !== '' && text.endsWith(suffix)
    ? text.slice(0, text.length - suffix.length)
    : text;
const parseNumber = (text) => text === ''
    ? NaN
    : Number(text.split(JS_DECIMAL_SEPARATOR, 2).join(JS_DECIMAL_SEPARATOR));
const groupInteger = (digits, separator) => separator === ''
    ? digits
    : digits.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
const resolveOptions = (options) => ({
    groupingSeparator: options.groupingSeparator ?? ' ',
    fractionSeparator: options.fractionSeparator ?? getSystemDecimalSeparator(),
    prefix: options.prefix ?? '',
    suffix: options.suffix ?? '',
    maximumIntegerDigits: Math.min(MAX_INTEGER_DIGITS, options.maximumIntegerDigits ?? MAX_INTEGER_DIGITS),
    maximumFractionalDigits: options.maximumFractionalDigits ?? 2,
    minValue: options.minValue,
    maxValue: options.maxValue,
    needBeforeUnmasking: options.needBeforeUnmasking ?? true,
});
const clamp = (text, minValue, maxValue) => {
    let result = text === JS_DECIMAL_SEPARATOR ? '0.' : text;
    if (typeof minValue === 'number') {
        const value = parseNumber(result);
        if (Number.isNaN(value)) {
            return '';
        }
        if (minValue > value) {
            result = String(minValue);
        }
    }
    if (typeof maxValue === 'number') {
        const value = parseNumber(result);
        if (Number.isNaN(value)) {
            return '';
        }
        if (maxValue < value) {
            result = String(maxValue);
        }
    }
    return result;
};
/** Strips the mask, returning a JS number string such as `"1234.5"`. */
export const unmaskText = (text, options) => {
    const { groupingSeparator, fractionSeparator, prefix, suffix } = resolveOptions(options);
    const withoutSeparators = replaceAll(replaceAll(text, groupingSeparator, ''), fractionSeparator, JS_DECIMAL_SEPARATOR);
    return removeSuffix(removePrefix(withoutSeparators, prefix), suffix).replace(/[^\d.]/g, '');
};
export const applyMaskToText = (text, options) => {
    const resolved = resolveOptions(options);
    const { groupingSeparator, fractionSeparator, prefix, suffix, maximumIntegerDigits, maximumFractionalDigits, } = resolved;
    const source = resolved.needBeforeUnmasking
        ? unmaskText(text, options)
        : text;
    const rawText = replaceAll(clamp(source, resolved.minValue, resolved.maxValue), JS_DECIMAL_SEPARATOR, fractionSeparator);
    if (rawText === '') {
        return '';
    }
    const [integerRaw, fraction] = rawText.split(fractionSeparator);
    const integerDigits = integerRaw
        .replace(/\D/g, '')
        .slice(0, maximumIntegerDigits)
        .replace(/^0+(?=\d)/, '') || '0';
    let result = groupInteger(integerDigits, groupingSeparator);
    if (fraction !== undefined && maximumFractionalDigits > 0) {
        result += fractionSeparator + fraction.slice(0, maximumFractionalDigits);
    }
    return prefix + result + suffix;
};
/**
 * Turns a newly typed system decimal separator into the fraction separator.
 * Only applies when text was added, so deleting back to a trailing grouping
 * separator that happens to match the system one is left alone.
 */
export const normalizeTypedText = (text, previousText, options) => {
    const { fractionSeparator, suffix } = resolveOptions(options);
    const typedSeparator = getSystemDecimalSeparator() + suffix;
    return text.length > previousText.length && text.endsWith(typedSeparator)
        ? text.slice(0, text.length - typedSeparator.length) +
            fractionSeparator +
            suffix
        : text;
};
/**
 * When a single grouping separator was deleted, also deletes the digit before
 * it, since the mask would otherwise put the separator straight back.
 */
export const deleteDigitBeforeSeparator = (text, caret, previousText, options) => {
    const { groupingSeparator, prefix } = resolveOptions(options);
    const deletedSeparator = groupingSeparator !== '' &&
        previousText.length === text.length + groupingSeparator.length &&
        previousText.startsWith(groupingSeparator, caret) &&
        text ===
            previousText.slice(0, caret) +
                previousText.slice(caret + groupingSeparator.length);
    return deletedSeparator && caret > prefix.length && /\d/.test(text[caret - 1])
        ? { text: text.slice(0, caret - 1) + text.slice(caret), caret: caret - 1 }
        : { text, caret };
};
/**
 * Maps a caret position in typed text to the same place in its masked text,
 * counting the digits and fraction separator before it. When the typed value
 * was clamped to `minValue`/`maxValue`, the caret goes to the end instead.
 */
export const caretAfterMask = (typedText, typedCaret, maskedText, options) => {
    const { fractionSeparator, prefix, suffix, minValue, maxValue } = resolveOptions(options);
    const isSignificant = (char) => /\d/.test(char) || char === fractionSeparator;
    const end = Math.max(prefix.length, maskedText.length - suffix.length);
    const typedValue = parseNumber(unmaskText(typedText, options));
    if ((typeof minValue === 'number' && typedValue < minValue) ||
        (typeof maxValue === 'number' && typedValue > maxValue)) {
        return end;
    }
    const typedStart = typedText.startsWith(prefix) ? prefix.length : 0;
    let remaining = typedText
        .slice(typedStart, Math.max(typedStart, typedCaret))
        .split('')
        .filter(isSignificant).length;
    let caret = prefix.length;
    while (remaining > 0 && caret < end) {
        if (isSignificant(maskedText[caret])) {
            remaining -= 1;
        }
        caret += 1;
    }
    return Math.min(remaining > 0 ? end : caret, maskedText.length);
};
